import { useState, useMemo } from "react";
import { toast } from "sonner";
import { useCategories, useUsers, useSetUserPermissions } from "../../queries";
import type { User } from "@/services/api/users";
import type { Category } from "@/services/api/categories";
import { sortUsers, type SortField } from "./userUtils";
import { useUsersSearchParams } from "./useUsersSearchParams";

export function useUsersPanel() {
  const { data: users = [], isLoading: isLoadingUsers } = useUsers();
  const { data: categories = [], isLoading: isLoadingCategories } =
    useCategories();
  const { mutateAsync: setUserPermissions, isPending: isUpdatingPermissions } =
    useSetUserPermissions();

  const { sorting, updateSearchParams } = useUsersSearchParams();
  const { sortField, sortDirection } = sorting;

  const [search, setSearch] = useState("");

  const filteredUsers = useMemo(() => {
    return users.filter((user: User) =>
      user.email.toLowerCase().includes(search.toLowerCase().trim())
    );
  }, [users, search]);

  const sortedUsers = useMemo<User[]>(() => {
    return sortUsers(filteredUsers, sortField, sortDirection);
  }, [filteredUsers, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      updateSearchParams({
        sortField: field,
        sortDirection: sortDirection === "asc" ? "desc" : "asc",
      });
    } else {
      updateSearchParams({
        sortField: field,
        sortDirection: "asc",
      });
    }
  };

  const handleTogglePermission = async (
    user: User,
    category: Category,
    checked: boolean
  ) => {
    if (!user.entraObjectId) {
      toast.error("Cannot update permissions", {
        description:
          "This user is missing an Entra object ID, so permissions cannot be updated. Please re-sync the user or contact an administrator.",
      });
      return;
    }

    const currentIds = user.categoryPermissions?.map((c) => c.categoryId) || [];
    const nextIds = checked
      ? Array.from(new Set([...currentIds, Number(category.id)]))
      : currentIds.filter((id: number) => Number(id) !== Number(category.id));

    await setUserPermissions({
      entraObjectId: user.entraObjectId,
      data: { categoryIds: nextIds },
    });
  };

  return {
    users: sortedUsers,
    categories,
    isLoading: isLoadingUsers,
    isLoadingCategories,
    isUpdatingPermissions,
    search,
    setSearch,
    sortField,
    sortDirection,
    handleSort,
    handleTogglePermission,
    usersCount: users.length,
  };
}
